// Warteseite fuer Konten, die noch nicht freigeschaltet sind.
//
// Projektplan 11.2: Bis zur Freischaltung sieht der Nutzer nur diese Seite,
// und die API liefert ihm fuer alle Datenendpunkte 403. Die Seite ist
// deshalb bewusst eine Sackgasse mit genau zwei Handlungen: Namen waehlen
// und abmelden.

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api, fehlertext } from "../lib/api.js";
import type { Nutzer } from "../lib/me.js";
import { Hinweis, Karte, Knopf, Rahmen } from "../bausteine/Bausteine.js";

export function Warten({ nutzer }: { nutzer: Nutzer }) {
  const [parameter] = useSearchParams();
  // Der Name von Google kommt nur als Vorschlag im Redirect mit und wird
  // nirgends gespeichert (siehe E4 in docs/decisions.md).
  const [name, setzeName] = useState(nutzer.displayName ?? parameter.get("name") ?? "");
  const queryClient = useQueryClient();

  const speichern = useMutation({
    mutationFn: () => api<{ displayName: string }>("/me", { method: "PATCH", body: { displayName: name } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["me"] }),
  });

  const abmelden = useMutation({
    mutationFn: () => api<void>("/auth/logout", { method: "POST" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["me"] }),
  });

  return (
    <Rahmen
      kinder={
        <>
          <Karte
            kinder={
              <>
                <h2 className="mb-1 text-base font-medium">Fast geschafft</h2>
                <p className="text-sm text-neutral-600">
                  Dein Konto <span className="font-medium">{nutzer.email}</span> wartet auf die
                  Freischaltung. Sobald sie da ist, geht es hier weiter.
                </p>
              </>
            }
          />

          <Karte
            kinder={
              <>
                <h2 className="mb-1 text-base font-medium">Wie sollen wir dich nennen?</h2>
                <p className="mb-3 text-sm text-neutral-600">
                  Dieser Name steht später an deinen Einträgen. Von Google übernehmen wir ihn
                  nicht, dort steht oft der Klarname.
                </p>

                {nutzer.displayName && !speichern.isPending && (
                  <Hinweis art="gut" kinder={`Gespeichert: ${nutzer.displayName}`} />
                )}
                {speichern.error && <Hinweis art="fehler" kinder={fehlertext(speichern.error)} />}

                <form
                  className="flex gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    speichern.mutate();
                  }}
                >
                  <input
                    value={name}
                    onChange={(e) => setzeName(e.target.value)}
                    placeholder="Anna"
                    maxLength={40}
                    className="min-h-11 flex-1 rounded border border-neutral-300 px-3 text-sm outline-none focus:border-akzent"
                  />
                  <Knopf type="submit" disabled={speichern.isPending || name.trim().length < 2} kinder="Speichern" />
                </form>
              </>
            }
          />

          <Knopf art="neben" onClick={() => abmelden.mutate()} kinder="Abmelden" />
        </>
      }
    />
  );
}
